import { motion } from "framer-motion";
import { GraduationCap, Award, Trophy } from "lucide-react";
import { education, certifications, achievements } from "../data/portfolio";
import { SectionHeader } from "./SectionHeader";

export const Education = () => {
  return (
    <section
      id="education"
      data-testid="section-education"
      className="relative py-24 md:py-32 px-5 sm:px-8"
    >
      <div className="max-w-6xl mx-auto">
        <SectionHeader
          testId="education-header"
          index="04 // education"
          title="Learning & credentials"
          sub="Academic foundation, certifications and moments worth remembering."
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Education */}
          <div className="lg:col-span-1">
            <div className="glow-card rounded-xl border border-white/10 bg-[#0d0620]/60 p-6 h-full">
              <div className="flex items-center gap-2 font-mono text-[11px] tracking-widest text-purple-400 uppercase mb-4">
                <GraduationCap size={14} /> Education
              </div>
              {education.map((e) => (
                <motion.div
                  key={e.institution}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4 }}
                  data-testid="education-item"
                >
                  <h3 className="text-xl font-semibold text-white">
                    {e.institution}
                  </h3>
                  <p className="text-purple-200 text-sm mt-1">{e.degree}</p>
                  <p className="mt-3 font-mono text-xs text-white/50">
                    {e.dates} · {e.location}
                  </p>
                  <div className="mt-4 inline-flex items-center px-3 py-1.5 rounded-md bg-purple-500/10 border border-purple-500/25 text-purple-200 text-xs font-mono">
                    {e.detail}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="glow-card rounded-xl border border-white/10 bg-[#0d0620]/60 p-6 md:col-span-2">
              <div className="flex items-center gap-2 font-mono text-[11px] tracking-widest text-purple-400 uppercase mb-4">
                <Award size={14} /> Certifications
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {certifications.map((c, i) => (
                  <motion.li
                    key={c.name}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.04 }}
                    data-testid={`certification-${i}`}
                    className="flex items-start gap-3"
                  >
                    <span className="mt-2 h-1 w-1 rounded-full bg-purple-400 flex-shrink-0" />
                    <div>
                      <div className="text-white/90 text-sm">{c.name}</div>
                      <div className="font-mono text-[11px] text-white/45 mt-0.5">
                        {c.issuer}
                        {c.year ? ` · ${c.year}` : ""}
                      </div>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="glow-card rounded-xl border border-white/10 bg-[#0d0620]/60 p-6 md:col-span-2">
              <div className="flex items-center gap-2 font-mono text-[11px] tracking-widest text-purple-400 uppercase mb-4">
                <Trophy size={14} /> Highlights
              </div>
              <ul className="space-y-2.5">
                {achievements.map((a, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-white/70 text-sm leading-relaxed"
                    data-testid={`achievement-${i}`}
                  >
                    <span className="mt-2 h-1 w-1 rounded-full bg-purple-400 flex-shrink-0" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
